import React, { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import tw from "tailwind-styled-components";
import Images from "../../../Images";

const DropZone = ({ setFieldValue, componentFor, setUploadFile, File, fields, existingImageUrl }) => {
    const [files, setFiles] = useState(File || []);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Cleanup preview URLs when component unmounts
        return () => {
            if (files && files.length > 0) {
                files.forEach(file => {
                    if (file.preview) {
                        URL.revokeObjectURL(file.preview);
                    }
                });
            }
        };
    }, []);

    useEffect(() => {
        if (componentFor === "profile") {
            setFieldValue("media", files);
        } else if (componentFor === "expense") {
            setFieldValue("file", files);
        } else if (componentFor === "admin") {
            setUploadFile(files);
        }
    }, [files, componentFor, setFieldValue, setUploadFile]);

    const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
        // Handle rejected files (size issues)
        if (rejectedFiles && rejectedFiles.length > 0) {
            const sizeError = rejectedFiles.find(
                rejection => rejection.errors && rejection.errors[0]?.code === 'file-too-large'
            );
            if (sizeError) {
                setError(`File "${sizeError.file.name}" exceeds 2MB limit`);
                // setTimeout(() => setError(null), 3000); // Clear error after 3 seconds
            }
            return;
        }

        // Clear any previous errors
        setError(null);

        if (acceptedFiles && acceptedFiles.length > 0) {
            const newFiles = acceptedFiles.map((file) =>
                Object.assign(file, {
                    preview: URL.createObjectURL(file),
                })
            );

            setFiles((prevFiles) => [...prevFiles, ...newFiles]);
        }
    }, []);

    const removeFile = (indexToRemove) => {
        setFiles((prevFiles) => {
            // Revoke object URL to avoid memory leaks
            if (prevFiles[indexToRemove]?.preview) {
                URL.revokeObjectURL(prevFiles[indexToRemove].preview);
            }
            return prevFiles.filter((_, index) => index !== indexToRemove);
        });
    };

    // Format accept as a string (this is what attr-accept expects)
    const getAcceptString = () => {
        return 'image/png, image/jpg, image/jpeg, image/webp, image/gif, image/bmp';
    };

    const minSize = 0;
    const maxSize = 2097152; // 2MB (2 * 1024 * 1024)
    const maxFiles = 0; // 0 means unlimited

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: getAcceptString(), // Pass as string, not object
        minSize,
        maxSize,
        maxFiles,
    });

    return (
        <div className="w-full">
            {/* Upload Area */}
            <div
                {...getRootProps()}
                className={`w-full rounded-md border border-gray-200 cursor-pointer ${isDragActive ? 'bg-gray-200' : ''
                    }`}
            >
                <input {...getInputProps()} />
                <div className="w-full grid place-content-center p-6">
                    <div className="flex flex-col items-center justify-center text-center">
                        <img src={Images.Upload} alt="upload-icon" className="w-8 mb-2" />
                        <p className="font-medium text-sm text-gray-600">
                            {isDragActive ? "Drop your images here" : "Click or Drag Images To Upload"}
                        </p>
                        <p className="text-xs text-gray-400">(JPG, PNG, JPEG, WEBP, GIF, BMP)</p>
                        <p className="text-xs text-gray-400 mt-1">Max file size: 2MB</p>
                        {files.length > 0 && (
                            <p className="text-sm text-green-600 mt-2">
                                {files.length} image{files.length > 1 ? 's' : ''} selected
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600">{error}</p>
                </div>
            )}

            {/* Preview Area - Show all uploaded images */}
            {files.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
                    {files.map((file, index) => (
                        <div key={index} className="relative aspect-square rounded-md overflow-hidden border border-gray-200 group">
                            <button
                                type="button"
                                onClick={() => removeFile(index)}
                                className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black bg-opacity-70 grid place-items-center cursor-pointer z-10 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-opacity-100"
                            >
                                <img src={Images.Cross} className="w-2" alt="remove" />
                            </button>
                            <img
                                src={file.preview}
                                className="object-cover w-full h-full"
                                alt={`preview-${index}`}
                            />
                            {/* Optional: Show file size warning if close to limit */}
                            {file.size > 1048576 && file.size <= 2097152 && (
                                <div className="absolute bottom-0 left-0 right-0 bg-yellow-500 bg-opacity-80 text-white text-xs text-center py-1">
                                    {(file.size / 1048576).toFixed(1)} MB
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default DropZone;